import { useMemo } from 'react';
import { HEAT_RAMP, heatLevel, type CalSession } from './calendarTypes';

/* The day at a glance, above the grid.
 *
 * Sixteen-odd cells, one per opening hour, shaded by how full that hour is
 * relative to the rest of the day — the quiet hours have to read as quiet even
 * at a gym that never fills a class. Clicking one scrolls the grid to it, so
 * the strip doubles as the scrollbar for a day you can't see all of. */

export interface HourCell {
  hour: number;
  sessions: number;
  fill: number;
}

export function TodayDayStrip({ sessions, startHour, endHour, now, isToday, viewport, onJump }: {
  /** Every session of the day after filters, cancelled ones included. */
  sessions: CalSession[];
  startHour: number;
  endHour: number;
  /** Gym-local minutes past midnight; only read when `isToday`. */
  now: number;
  isToday: boolean;
  /** The hour range currently on screen in the grid — drawn as an outline. */
  viewport: [number, number];
  onJump: (hour: number) => void;
}) {
  const cells = useMemo<HourCell[]>(() => {
    const out: HourCell[] = [];
    for (let h = startHour; h < endHour; h++) {
      const inHour = sessions.filter(s => s.status !== 'cancelled' && Math.floor(s.start / 60) === h);
      const booked = inHour.reduce((a, s) => a + s.booked, 0);
      const cap = inHour.reduce((a, s) => a + s.cap, 0);
      out.push({ hour: h, sessions: inHour.length, fill: cap ? booked / cap : 0 });
    }
    return out;
  }, [sessions, startHour, endHour]);

  /* Normalised against the day's own busiest and quietest hour, not against
     100% — a gym that runs at 40% all morning still has a shape. */
  const busy = cells.filter(c => c.sessions);
  const lo = busy.length ? Math.min(...busy.map(c => c.fill)) : 0;
  const hi = busy.length ? Math.max(...busy.map(c => c.fill)) : 0;

  const live = sessions.filter(s => s.status !== 'cancelled');
  const booked = live.reduce((a, s) => a + s.booked, 0);
  const cap = live.reduce((a, s) => a + s.cap, 0);

  return (
    <div className="a-ds-strip">
      <p className="a-ds-sum">
        <b>{live.length}</b> session{live.length === 1 ? '' : 's'}
        <em>·</em>
        <b>{booked}</b>/{cap} booked
        <em>·</em>
        <b>{cap ? Math.round((booked / cap) * 100) : 0}%</b>
      </p>
      <div className="a-ds-cells" style={{ gridTemplateColumns: `repeat(${cells.length}, minmax(0,1fr))` }}>
        {cells.map(c => {
          const v = c.sessions ? Math.max(0.01, (c.fill - lo) / Math.max(0.01, hi - lo)) : 0;
          const past = isToday && (c.hour + 1) * 60 <= now;
          const inView = c.hour >= viewport[0] && c.hour < viewport[1];
          return (
            <button
              key={c.hour}
              className={`a-ds-c${past ? ' past' : ''}${inView ? ' inview' : ''}`}
              style={{ background: HEAT_RAMP[c.sessions ? heatLevel(v) : 0] }}
              onClick={() => onJump(c.hour)}
              title={`${String(c.hour).padStart(2, '0')}:00 · ${c.sessions} session${c.sessions === 1 ? '' : 's'} · ${Math.round(c.fill * 100)}%`}
              aria-label={`Scroll to ${String(c.hour).padStart(2, '0')}:00 — ${c.sessions} session${c.sessions === 1 ? '' : 's'}, ${Math.round(c.fill * 100)} percent booked`}
            >
              <span>{String(c.hour).padStart(2, '0')}</span>
              {isToday && Math.floor(now / 60) === c.hour && (
                <i style={{ left: `${((now % 60) / 60) * 100}%` }} aria-hidden="true" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
